import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Upload, X, ArrowLeft } from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#263562]/15 rounded-lg px-4 py-2.5 text-sm text-[#1E2A4A] placeholder:text-[#949CA1] outline-none focus:border-[#3AB0E1] focus:ring-1 focus:ring-[#3AB0E1]/30 transition-all";
const labelCls =
  "block font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#949CA1] mb-1.5";

const EMPTY_FORM = {
  id: "",
  name: "",
  category: "hti",
  badge: "HTI Distribution",
  tagline: "",
  description: "",
  features: "",
  highlights: "",
  idealFor: "",
  image: "",
  gallery: [],
  link: "",
  order: 99,
};

const toForm = (p) => ({
  ...EMPTY_FORM,
  ...p,
  image: p.image || "",
  features: (p.features || []).join("\n"),
  highlights: (p.highlights || []).join("\n"),
  idealFor: (p.idealFor || []).join(", "),
  gallery: (p.gallery || []).map((g) => ({ ...g })),
});

const toPayload = (f) => ({
  id: f.id.trim(),
  name: f.name.trim(),
  category: f.category,
  badge: f.badge,
  tagline: f.tagline,
  description: f.description,
  features: f.features.split("\n").map((s) => s.trim()).filter(Boolean),
  highlights: f.highlights.split("\n").map((s) => s.trim()).filter(Boolean),
  idealFor: f.idealFor.split(",").map((s) => s.trim()).filter(Boolean),
  image: f.image.trim() || null,
  gallery: f.gallery.filter((g) => g.src.trim()),
  link: f.link.trim(),
  order: Number(f.order) || 0,
});

const readImage = (file, cb) => {
  if (!file) return;
  if (file.size > 1.5 * 1024 * 1024) {
    toast.error("Image too large", { description: "Please keep uploads under 1.5MB." });
    return;
  }
  const reader = new FileReader();
  reader.onload = () => cb(reader.result);
  reader.readAsDataURL(file);
};

function ImageField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex gap-2">
        <input
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… or upload"
          className={inputCls}
        />
        <label className="shrink-0 flex items-center gap-1.5 cursor-pointer border border-[#263562]/15 hover:border-[#3AB0E1] rounded-lg px-3 text-xs text-[#263562] transition-colors">
          <Upload className="w-3.5 h-3.5" />
          Upload
          <input type="file" accept="image/*" className="hidden" onChange={(e) => readImage(e.target.files[0], onChange)} />
        </label>
      </div>
      {value && (
        <img src={value} alt="preview" className="mt-2 h-16 rounded-md border border-[#263562]/10 object-cover" />
      )}
    </div>
  );
}

export default function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/products").then((r) => setProducts(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const startNew = () => {
    setForm(EMPTY_FORM);
    setEditing("new");
  };

  const startEdit = (p) => {
    setForm(toForm(p));
    setEditing(p.id);
  };

  const save = async () => {
    if (!form.id.trim() || !form.name.trim()) {
      toast.error("ID and Name are required");
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (editing === "new") {
        await api.post("/admin/products", payload);
        toast.success(`"${payload.name}" created`);
      } else {
        await api.put(`/admin/products/${editing}`, payload);
        toast.success(`"${payload.name}" updated`);
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" from the website?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success(`"${p.name}" deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setGallery = (i, k, v) => {
    const gallery = [...form.gallery];
    gallery[i] = { ...gallery[i], [k]: v };
    setForm({ ...form, gallery });
  };

  if (editing) {
    return (
      <div data-testid="product-editor" className="bg-white rounded-2xl border border-[#263562]/10 p-8 shadow-sm">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-display font-semibold text-2xl text-[#263562]">
            {editing === "new" ? "Add New Product" : `Edit: ${form.name}`}
          </h2>
          <button
            data-testid="product-editor-back"
            onClick={() => setEditing(null)}
            className="flex items-center gap-2 text-sm text-[#5A6B85] hover:text-[#263562] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to list
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className={labelCls}>Product ID (slug) *</label>
            <input data-testid="product-editor-id" value={form.id} onChange={setF("id")} disabled={editing !== "new"} placeholder="my-product" className={`${inputCls} disabled:bg-[#F5F8FC] disabled:text-[#949CA1]`} />
          </div>
          <div>
            <label className={labelCls}>Name *</label>
            <input data-testid="product-editor-name" value={form.name} onChange={setF("name")} placeholder="My Product" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Category</label>
            <select data-testid="product-editor-category" value={form.category} onChange={setF("category")} className={inputCls}>
              <option value="proprietary">HostPoint — In-House</option>
              <option value="hti">HTI Distribution</option>
              <option value="partners">Partners</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Badge Label</label>
            <select data-testid="product-editor-badge" value={form.badge} onChange={setF("badge")} className={inputCls}>
              <option>In-House Flagship</option>
              <option>HTI Distribution</option>
              <option>Distribution Partner</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Official Site Link</label>
            <input data-testid="product-editor-link" value={form.link} onChange={setF("link")} placeholder="https://…" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Display Order</label>
            <input data-testid="product-editor-order" type="number" value={form.order} onChange={setF("order")} className={inputCls} />
          </div>
        </div>

        <div className="mt-5">
          <label className={labelCls}>Tagline (shown on the card)</label>
          <input data-testid="product-editor-tagline" value={form.tagline} onChange={setF("tagline")} className={inputCls} />
        </div>
        <div className="mt-5">
          <label className={labelCls}>Full Description (shown in detail view)</label>
          <textarea data-testid="product-editor-description" rows={3} value={form.description} onChange={setF("description")} className={`${inputCls} resize-y`} />
        </div>
        <div className="grid sm:grid-cols-2 gap-5 mt-5">
          <div>
            <label className={labelCls}>Card Features (one per line)</label>
            <textarea data-testid="product-editor-features" rows={4} value={form.features} onChange={setF("features")} className={`${inputCls} resize-y`} />
          </div>
          <div>
            <label className={labelCls}>Key Capabilities (one per line)</label>
            <textarea data-testid="product-editor-highlights" rows={4} value={form.highlights} onChange={setF("highlights")} className={`${inputCls} resize-y`} />
          </div>
        </div>
        <div className="mt-5">
          <label className={labelCls}>Ideal For (comma separated)</label>
          <input data-testid="product-editor-idealfor" value={form.idealFor} onChange={setF("idealFor")} placeholder="Hotels, Lodges, Restaurants" className={inputCls} />
        </div>

        <div className="mt-5">
          <ImageField label="Card Image" value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="product-editor-image" />
        </div>

        <div className="mt-7">
          <div className="flex items-center justify-between mb-3">
            <label className={`${labelCls} mb-0`}>Gallery (detail view screenshots)</label>
            <button
              data-testid="product-editor-add-gallery"
              onClick={() => setForm({ ...form, gallery: [...form.gallery, { src: "", caption: "" }] })}
              className="flex items-center gap-1.5 text-xs font-medium text-[#263562] border border-[#263562]/15 hover:border-[#3AB0E1] rounded-full px-3.5 py-1.5 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Add image
            </button>
          </div>
          <div className="space-y-3">
            {form.gallery.map((g, i) => (
              <div key={i} className="flex gap-2 items-start bg-[#F5F8FC] rounded-lg p-3">
                <div className="flex-1 grid sm:grid-cols-2 gap-2">
                  <input
                    data-testid={`product-editor-gallery-src-${i}`}
                    value={g.src}
                    onChange={(e) => setGallery(i, "src", e.target.value)}
                    placeholder="Image URL or upload →"
                    className={inputCls}
                  />
                  <input
                    value={g.caption}
                    onChange={(e) => setGallery(i, "caption", e.target.value)}
                    placeholder="Caption"
                    className={inputCls}
                  />
                </div>
                <label className="shrink-0 flex items-center cursor-pointer border border-[#263562]/15 hover:border-[#3AB0E1] rounded-lg px-3 py-2.5 text-xs text-[#263562] transition-colors">
                  <Upload className="w-3.5 h-3.5" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => readImage(e.target.files[0], (v) => setGallery(i, "src", v))} />
                </label>
                <button
                  data-testid={`product-editor-gallery-remove-${i}`}
                  onClick={() => setForm({ ...form, gallery: form.gallery.filter((_, j) => j !== i) })}
                  className="shrink-0 p-2.5 text-[#949CA1] hover:text-red-500 transition-colors"
                  aria-label="Remove image"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            {form.gallery.length === 0 && (
              <p className="text-xs text-[#949CA1]">No gallery images — the detail view will show a branded placeholder.</p>
            )}
          </div>
        </div>

        <div className="mt-8 flex items-center gap-4">
          <button
            data-testid="product-editor-save"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 bg-[#263562] hover:bg-[#3AB0E1] disabled:opacity-60 text-white font-semibold px-7 py-3 rounded-full transition-colors duration-300"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editing === "new" ? "Create Product" : "Save Changes"}
          </button>
          <button onClick={() => setEditing(null)} className="text-sm text-[#5A6B85] hover:text-[#263562] transition-colors">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#5A6B85]">
          {products.length} product{products.length === 1 ? "" : "s"} live on the website
        </p>
        <button
          data-testid="admin-product-new"
          onClick={startNew}
          className="flex items-center gap-2 bg-[#263562] hover:bg-[#3AB0E1] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-300"
        >
          <Plus className="w-4 h-4" /> Add Product
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-[#3AB0E1]" />
        </div>
      ) : (
        <div className="space-y-3">
          {products.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-product-row-${p.id}`}
              className="bg-white rounded-xl border border-[#263562]/10 px-5 py-4 flex items-center gap-4 hover:border-[#3AB0E1]/40 transition-colors"
            >
              {p.image ? (
                <img src={p.image} alt="" className="w-14 h-14 rounded-lg object-cover border border-[#263562]/10 shrink-0" />
              ) : (
                <span className="w-14 h-14 rounded-lg bg-[#F5F8FC] border border-[#263562]/10 flex items-center justify-center font-display font-semibold text-[#263562]/40 shrink-0">
                  {p.name.slice(0, 2).toUpperCase()}
                </span>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#263562]">{p.name}</p>
                <p className="text-xs text-[#949CA1] truncate">
                  {p.badge} · order {p.order} · {(p.gallery || []).length} gallery image{(p.gallery || []).length === 1 ? "" : "s"}
                </p>
              </div>
              <button
                data-testid={`admin-product-edit-${p.id}`}
                onClick={() => startEdit(p)}
                className="flex items-center gap-1.5 text-xs font-medium text-[#263562] border border-[#263562]/15 hover:border-[#3AB0E1] rounded-full px-4 py-2 transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
              <button
                data-testid={`admin-product-delete-${p.id}`}
                onClick={() => remove(p)}
                className="flex items-center gap-1.5 text-xs font-medium text-red-500 border border-red-200 hover:bg-red-50 rounded-full px-4 py-2 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          ))}
          {products.length === 0 && (
            <p className="text-center text-sm text-[#949CA1] py-12">No products yet — add your first one.</p>
          )}
        </div>
      )}
    </div>
  );
}
